import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Bot,
  Sparkles,
  MessageSquare,
  MessagesSquare,
  FileText,
  BarChart3,
  Settings,
  LogOut,
  PanelLeftClose,
  PanelLeft,
} from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import ConfirmDialog from '../ui/ConfirmDialog';
import logo from '../../assets/botibonga_logo.svg';

export default function AdminSidebar() {
  const navigate = useNavigate();
  const { admin, signOut } = useAdminAuth();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showSignOutDialog, setShowSignOutDialog] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Sign out failed:', error);
      setIsSigningOut(false);
      setShowSignOutDialog(false);
    }
  };

  const navItems = [
    { to: '/', icon: LayoutDashboard, label: 'Dashboard', end: true },
    { to: '/users', icon: Users, label: 'Users' },
    { to: '/agents', icon: Bot, label: 'Agents' },
    { to: '/conversations', icon: MessagesSquare, label: 'Conversations' },
    { to: '/requests', icon: Sparkles, label: 'Custom Requests' },
    { to: '/feedback', icon: MessageSquare, label: 'Feedback' },
    { to: '/docs/editor', icon: FileText, label: 'Documentation' },
    { to: '/analytics', icon: BarChart3, label: 'Analytics' },
  ];

  const bottomItems = [
    { to: '/settings', icon: Settings, label: 'Settings' },
  ];

  return (
    <aside
      className={`${
        isCollapsed ? 'w-20' : 'w-64'
      } bg-white border-r border-border flex flex-col h-screen sticky top-0 transition-all duration-300 ease-in-out`}
    >
      {/* Logo & Toggle */}
      <div
        className={`p-4 border-b border-border flex ${
          isCollapsed ? 'flex-col items-center gap-3' : 'items-center justify-between'
        }`}
      >
        {!isCollapsed ? (
          <div className="flex items-center gap-3">
            <img src={logo} alt="Botibonga" className="w-10 h-10" />
            <div>
              <h1 className="font-bold text-text-primary">Admin</h1>
              <p className="text-xs text-text-muted">Botibonga</p>
            </div>
          </div>
        ) : (
          <img src={logo} alt="Botibonga" className="w-8 h-8" />
        )}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-2 rounded-lg hover:bg-primary/10 text-primary transition-colors"
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? (
            <PanelLeft className="w-5 h-5" />
          ) : (
            <PanelLeftClose className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className={`flex-1 ${isCollapsed ? 'p-2' : 'p-4'} space-y-1 overflow-y-auto`}>
        {navItems.map(({ to, icon: Icon, label, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            title={isCollapsed ? label : undefined}
            className={({ isActive }) =>
              `sidebar-link ${isActive ? 'active' : ''} ${
                isCollapsed ? 'justify-center px-3' : ''
              }`
            }
          >
            <Icon className="w-5 h-5 flex-shrink-0" />
            {!isCollapsed && <span>{label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Bottom Section */}
      <div
        className={`${isCollapsed ? 'p-2' : 'p-4'} border-t border-border space-y-1`}
      >
        {bottomItems.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            title={isCollapsed ? label : undefined}
            className={({ isActive }) =>
              `sidebar-link ${isActive ? 'active' : ''} ${
                isCollapsed ? 'justify-center px-3' : ''
              }`
            }
          >
            <Icon className="w-5 h-5 flex-shrink-0" />
            {!isCollapsed && <span>{label}</span>}
          </NavLink>
        ))}

        {/* Admin Info & Sign Out */}
        {!isCollapsed && admin && (
          <div className="pt-3 mt-3 border-t border-border">
            <button
              onClick={() => navigate('/profile')}
              className="flex items-center gap-3 px-2 mb-3 w-full rounded-lg hover:bg-primary/5 py-2 transition-colors"
            >
              {admin.picture ? (
                <img
                  src={admin.picture}
                  alt={admin.name}
                  className="w-8 h-8 rounded-full"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-sm font-medium text-primary">
                    {admin.name?.charAt(0) || 'A'}
                  </span>
                </div>
              )}
              <div className="flex-1 min-w-0 text-left">
                <p className="text-sm font-medium text-text-primary truncate">
                  {admin.name}
                </p>
                <p className="text-xs text-text-muted truncate">{admin.email}</p>
              </div>
            </button>
          </div>
        )}

        {isCollapsed && admin && (
          <button
            onClick={() => navigate('/profile')}
            title="View Profile"
            className="sidebar-link justify-center px-3 mb-1"
          >
            {admin.picture ? (
              <img
                src={admin.picture}
                alt={admin.name}
                className="w-6 h-6 rounded-full"
              />
            ) : (
              <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-xs font-medium text-primary">
                  {admin.name?.charAt(0) || 'A'}
                </span>
              </div>
            )}
          </button>
        )}

        <button
          onClick={() => setShowSignOutDialog(true)}
          title={isCollapsed ? 'Sign Out' : undefined}
          className={`sidebar-link w-full text-danger hover:bg-danger/10 hover:text-danger ${
            isCollapsed ? 'justify-center px-3' : 'text-left'
          }`}
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          {!isCollapsed && <span>Sign Out</span>}
        </button>
      </div>

      {/* Sign Out Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showSignOutDialog}
        onClose={() => setShowSignOutDialog(false)}
        onConfirm={handleSignOut}
        title="Sign Out"
        message="Are you sure you want to sign out? You will need to sign in again to access the admin panel."
        confirmText="Sign Out"
        cancelText="Cancel"
        variant="danger"
        isLoading={isSigningOut}
      />
    </aside>
  );
}
